import { TableName } from '../../../../tables'
import { UIPermission, DetailPanel, DetailFieldProps, Tab, FieldType, StockAction, useGetCrumbs, Spinner, Async } from '../../../../features/ui'
import { define } from '../../../../utils/typeUtils'
import { useSystemStateHooks } from '../../../system'
import { Action } from '../../../../features/ui'
import { Stock } from '../../state/types'
import { Product, ProductName, useProductLoaders } from '../../../products'
import { BranchStockTables } from '../branch-stock-tables/branchStockTables'
import { useManagerState } from '../../../manager'
import { useEffect, useMemo, useState } from 'react'
import { useStockHooks } from '../../hooks/stockHooks'
import { RoutePath, useRoutesHooks } from '../../../../features/routes'
import { useNavigate } from 'react-router-dom'

export const StockDetail = () => {
  const { dataType } = useRoutesHooks().parseUrl()
  const urlStock = define(dataType) as Stock

  const [stock, setStock] = useState<Stock>(urlStock)
  const [product, setProduct] = useState<Product>()

  const { currentBranch } = useManagerState()
  const branch = define(currentBranch)

  const { userHasPermission } = useSystemStateHooks()
  const readOnly = !userHasPermission(UIPermission.SAVE_BUDGETS)
  const { describeAvailableStock, describeReservedStock, describeSoldWithoutStock, getNoLotStock, getStockActionRoute } = useStockHooks()
  const [soldWithoutStock, setSoldWithoutStock] = useState<string>()
  const navigate = useNavigate()

  const productLoaders = useProductLoaders()
  
  useEffect(() => {
    productLoaders().loadProduct(stock.productId).then(setProduct)
  }, [])

  useEffect(() => {
    getNoLotStock(stock.productId, branch.id).then(setStock)
  }, [branch])

  useEffect(() => {
    product && describeSoldWithoutStock(product, branch.id).then(setSoldWithoutStock)
  }, [product, branch])

  const stockData = useMemo(() => ({
    ...stock,
    imageUrl: product?.imageUrl
  }), [stock, product])
  
  const crumbs = useGetCrumbs(
    'Stock',
    RoutePath.STOCK,
    product?.name
  )

  if (!product) return <Spinner />

  const title = `Stock: ${product?.name}`

  const fields: DetailFieldProps[] = [
    {
      name: 'productCode',
      type: FieldType.TEXT,
      label: 'Código',
      render: () => <>#{product.code}</>
    },
    {
      name: 'productSupplierCode',
      type: FieldType.TEXT,
      label: 'Código Interno',
      render: () => <>{product.supplierCode}</>
    },
    {
      name: 'product',
      type: FieldType.TEXT,
      label: 'Producto',
      render: () => <ProductName product={product} />
    },
    {
      name: 'branchId',
      type: FieldType.TABLE,
      table: TableName.BRANCHES,
      label: 'Sucursal'
    },
    {
      name: 'availableUnits',
      type: FieldType.TEXT,
      label: 'Stock Disponible',
      render: () => <Async
        loadFn={async () => <>{await describeAvailableStock(product, branch.id)}</>}
        deps={[product, branch.id]}
      />
    },
    {
      name: 'reservedUnits',
      type: FieldType.TEXT,
      label: 'Stock Reservado',
      render: () => <Async
        loadFn={async () => <>{await describeReservedStock(product, branch.id)}</>}
        deps={[product, branch.id]}
      />
    }
  ]

  if (soldWithoutStock) {
    fields.push({
      name: 'soldWithoutStock',
      type: FieldType.TEXT,
      label: 'Vendido Sin Stock',
      render: () => <span className="text-danger">{soldWithoutStock}</span>
    })
  }

  const renderLotsStockContent = (
    <BranchStockTables productId={product.id} />
  )

  const tabs: Tab[] = [
    { id: 'lots-stock', title: 'Partidas', content: renderLotsStockContent },
  ]

  const onEditStock = (stockAction: StockAction) => {
    const actionPath = getStockActionRoute(stockAction)
    navigate(actionPath)
  }

  const onIncreaseStock = () => onEditStock(StockAction.ADD)
  const onDecreaseStock = () => onEditStock(StockAction.SUBSTRACT)

  const onTransform = () => navigate(RoutePath.TRANSFORM)

  const actions: Action[] = []
  if (!readOnly) {
    actions.push(
      { label: 'Alta manual', icon: 'plus-circle-fill', onClick: onIncreaseStock },
      { label: 'Baja manual', icon: 'dash-circle-fill', kind: 'secondary', onClick: onDecreaseStock }
    )
    if (product.equivalences && product.equivalences.length > 0) {
      actions.splice(1, 0, { label: 'Transformar', icon: 'arrow-left-right', onClick: onTransform })
    }
  }

  return (
    <DetailPanel
      title={title}
      tableName={TableName.STOCK}
      crumbs={crumbs}
      fields={fields}
      data={stockData}
      icon='boxes'
      tabs={tabs}
      readOnly={readOnly}
      actions={actions}
    />
  )
}
